import { createFileRoute, useSearch } from '@tanstack/react-router';
import BulldogFive from '@/components/dashboard/BulldogFive';
import StatCard from '@/components/dashboard/StatCard';
import TraditionalStatDisplay from '@/components/dashboard/TraditionalStats';
import RadarV2 from '@/lib/charts/RadarV2';
import ScoreCard from '@/lib/charts/ScoreCard';
import {
    ApproachStrokesGainedMapping,
    AroundGreenStrokesGainedMapping,
    OverallStrokesGained,
    PuttingStrokesGainedMapping,
    SGTabType,
} from '@/lib/models/strokesGained';
import useRound from '@/lib/rounds/hooks/useRound';
import useRoundStrokesGained from '@/lib/rounds/hooks/useRoundStrokesGained';
import StatsTabs from '@/lib/strokesGained/Components/StatsTab';

export const Route = createFileRoute('/rounds/$roundId/')({
    component: RouteComponent,
});

function RouteComponent() {
    const { roundId } = Route.useParams();
    const { round } = useRound({ id: parseInt(roundId ?? '0') });

    const searchParams = useSearch({ strict: false }) as { tab?: string };
    const activeTab = searchParams.tab ?? 'strokesGained';

    const { strokesGained } = useRoundStrokesGained({ roundId: parseInt(roundId ?? '0') });

    return (
        <div>
            {round && <ScoreCard round={round} strokesGained={strokesGained} />}
            <div className='mt-2'>
                <StatsTabs individualRound />
            </div>
            {!!strokesGained?.strokes_gained_round && (
                <>
                    {activeTab === 'strokesGained' && (
                        <div className='flex w-full flex-col-reverse gap-6 md:grid md:grid-cols-2 md:items-center '>
                            <div className='p-3'>
                                <div className='mb-4 grid gap-4 md:grid-cols-3'>
                                    <StatCard
                                        title={OverallStrokesGained.total}
                                        value={strokesGained.strokes_gained_round.total}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={OverallStrokesGained.tee}
                                        value={strokesGained.strokes_gained_round.tee}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={OverallStrokesGained.approach}
                                        value={strokesGained.strokes_gained_round.approach}
                                        bottomLabel=''
                                    />
                                </div>
                                <div className='grid gap-4 md:grid-cols-3'>
                                    <StatCard
                                        title={OverallStrokesGained.around_green}
                                        value={strokesGained.strokes_gained_round.around_green}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={OverallStrokesGained.sand}
                                        value={strokesGained.strokes_gained_round.sand}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={OverallStrokesGained.putting}
                                        value={strokesGained.strokes_gained_round.putting}
                                        bottomLabel=''
                                    />
                                </div>
                            </div>

                            <div className=''>
                                <RadarV2
                                    strokesGained={strokesGained.strokes_gained_round}
                                    sgType={activeTab as SGTabType}
                                />
                            </div>
                        </div>
                    )}
                    {activeTab === 'approach' && (
                        <div className='flex w-full flex-col-reverse gap-6 md:grid md:grid-cols-2 md:items-center '>
                            <div className='p-3'>
                                <div className='grid gap-4 md:grid-cols-3'>
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.fairway_50_to_100}
                                        value={strokesGained.strokes_gained_round.fairway_50_to_100}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.fairway_100_to_150}
                                        value={strokesGained.strokes_gained_round.fairway_100_to_150}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.fairway_150_to_200}
                                        value={strokesGained.strokes_gained_round.fairway_150_to_200}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.fairway_outside_200}
                                        value={strokesGained.strokes_gained_round.fairway_outside_200}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.rough_inside_150}
                                        value={strokesGained.strokes_gained_round.rough_inside_150}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={ApproachStrokesGainedMapping.rough_outside_150}
                                        value={strokesGained.strokes_gained_round.rough_outside_150}
                                        bottomLabel=''
                                    />
                                </div>
                            </div>
                            <div className=''>
                                <RadarV2
                                    strokesGained={strokesGained.strokes_gained_round}
                                    sgType={activeTab as SGTabType}
                                />
                            </div>
                        </div>
                    )}
                    {activeTab === 'aroundGreen' && (
                        <div className='flex w-full flex-col-reverse gap-6 md:grid md:grid-cols-2 md:items-center '>
                            <div className='p-3'>
                                <div className='grid gap-4 md:grid-cols-3'>
                                    <StatCard
                                        title={AroundGreenStrokesGainedMapping.fairway_around_green_30}
                                        value={strokesGained.strokes_gained_round.fairway_around_green_30}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={AroundGreenStrokesGainedMapping.fairway_around_green_15}
                                        value={strokesGained.strokes_gained_round.fairway_around_green_15}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={AroundGreenStrokesGainedMapping.rough_around_green_30}
                                        value={strokesGained.strokes_gained_round.rough_around_green_30}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={AroundGreenStrokesGainedMapping.rough_around_green_15}
                                        value={strokesGained.strokes_gained_round.rough_around_green_15}
                                        bottomLabel=''
                                    />
                                </div>
                            </div>
                            <div className=''>
                                <RadarV2
                                    strokesGained={strokesGained.strokes_gained_round}
                                    sgType={activeTab as SGTabType}
                                />
                            </div>
                        </div>
                    )}
                    {activeTab === 'putting' && (
                        <div className='flex w-full flex-col-reverse gap-6 md:grid md:grid-cols-2 md:items-center '>
                            <div className='p-3'>
                                <div className='grid gap-4 md:grid-cols-3'>
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_inside_5}
                                        value={strokesGained.strokes_gained_round.putting_inside_5}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_5_to_10}
                                        value={strokesGained.strokes_gained_round.putting_5_to_10}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_10_to_15}
                                        value={strokesGained.strokes_gained_round.putting_10_to_15}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_15_to_20}
                                        value={strokesGained.strokes_gained_round.putting_15_to_20}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_20_to_30}
                                        value={strokesGained.strokes_gained_round.putting_20_to_30}
                                        bottomLabel=''
                                    />
                                    <StatCard
                                        title={PuttingStrokesGainedMapping.putting_outside_30}
                                        value={strokesGained.strokes_gained_round.putting_outside_30}
                                        bottomLabel=''
                                    />
                                </div>
                            </div>
                            <div className=''>
                                <RadarV2
                                    strokesGained={strokesGained.strokes_gained_round}
                                    sgType={activeTab as SGTabType}
                                />
                            </div>
                        </div>
                    )}
                    {activeTab === 'traditional' && (
                        <TraditionalStatDisplay
                            traditionalStats={strokesGained.traditional_stats}
                            noRound
                            noLabel
                        />
                    )}
                    {activeTab === 'bulldogFive' && (
                        <BulldogFive bulldogFive={strokesGained.bulldog_five} noRound noLabel />
                    )}
                </>
            )}
        </div>
    );
}
